import { useState, useEffect, useCallback, useRef } from 'react'
import { Trash2, ChevronDown, ChevronUp, Settings } from 'lucide-react'
import { useDashboardStore } from '../../store/dashboardStore'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import ConfirmModal from '../common/ConfirmModal'
import { botApi } from '../../services/botApi'
import { getMe } from '../../services/api'
import { formatPnlNoRound } from '../../utils/formatCurrency'
// Порог фандинга вводится без минуса: знак нарисован слева и не стирается,
// а в поле лежит модуль числа (2026-10-21, см. src/utils/fundingThreshold.js).
import { fundingMagnitude, fundingDraft, fundingSigned, parseThresholdRange } from '../../utils/fundingThreshold'

const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10]
const ORDER_COUNTS     = [1, 2, 3, 4, 5]

// Как часто автоматически перезапрашивать статистику закрытых сделок,
// пока карточка бота смонтирована (мс). Не слишком часто, чтобы не
// нагружать бэкенд чтением файла PNL на каждый чих.
const STATS_POLL_INTERVAL_MS = 15000

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 12,
  background: '#141414', border: '1px solid rgba(255,255,255,0.08)',
  color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
}

const pctSuffix = {
  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
  fontSize: 11, color: '#636366', fontWeight: 600, pointerEvents: 'none',
}

// Знак минуса в полях порога фандинга стоит заранее и не стирается
// (2026-10-21): в поле вводится только цифра.
const minusPrefix = {
  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
  fontSize: 14, color: '#fff', fontWeight: 700, pointerEvents: 'none', userSelect: 'none',
}

function formatTime(minutes, t) {
  if (minutes < 60) return `${minutes}${t.timeMin}`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}${t.timeHour} ${m}${t.timeMin}` : `${h}${t.timeHour}`
}

// Форматирование даты: 2026-06-10 00:13:13
function formatDateTime(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const subLabel = (text, color = '#636366') => (
  <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>
    {text}
  </div>
)


function BotToggle({ active, onChange, t, disabled = false, onLockedPress }) {
  // onLockedPress: ползунок визуально заблокирован (подписка истекла), но тап
  // должен показать уведомление, а не молча ничего не делать. Поэтому нативный
  // disabled не ставим (иначе тап вообще не дойдёт), а клик обрабатываем сами.
  const handleClick = onLockedPress || (disabled ? undefined : onChange)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%', background: disabled ? '#636366' : '#FF3B30',
        boxShadow: active ? 'none' : (disabled ? 'none' : '0 0 7px rgba(255,59,48,0.8)'),
        opacity: active ? 0.3 : (disabled ? 0.3 : 1), transition: 'all 0.25s',
      }} />

      <button
        onClick={handleClick}
        role="switch"
        aria-checked={active}
        aria-disabled={disabled}
        disabled={disabled && !onLockedPress}
        style={{
          width: 46, height: 26, borderRadius: 13,
          background: disabled
            ? 'rgba(255,255,255,0.08)'
            : (active
              ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
              : 'rgba(255,255,255,0.08)'),
          position: 'relative', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 0.25s', flexShrink: 0,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div style={{
          position: 'absolute', top: 3,
          left: active ? 23 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: disabled ? '#636366' : '#fff', transition: 'left 0.25s',
          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }} />
      </button>

      <div style={{
        width: 8, height: 8, borderRadius: '50%', background: disabled ? '#636366' : '#34D399',
        boxShadow: active ? (disabled ? 'none' : '0 0 7px rgba(52,199,89,0.8)') : 'none',
        opacity: active ? (disabled ? 0.3 : 1) : 0.3, transition: 'all 0.25s',
      }} />
    </div>
  )
}

function InfoRow({ label, value, valueColor, last }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingTop: 8, paddingBottom: 8,
      borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{ fontSize: 13, color: '#636366' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: valueColor ?? '#8E8E93' }}>{value}</span>
    </div>
  )
}

export default function BotCard({ bot }) {
  const t = useT()
  const haptic = useHaptic()
  const toggleUserBot  = useDashboardStore(s => s.toggleUserBot)
  const removeUserBot  = useDashboardStore(s => s.removeUserBot)
  const updateUserBot  = useDashboardStore(s => s.updateUserBot)
  // Доступность типа счёта бота по подписке. Если подписка истекла (или юзер
  // не админ, а тип счёта недоступен) — ползунок блокируется: переключить
  // нельзя, но тап показывает уведомление botSubExpired.
  const demoAllowed = useDashboardStore(s => s.demoAllowed)
  const realAllowed = useDashboardStore(s => s.realAllowed)
  const isAdmin     = useDashboardStore(s => s.isAdmin)
  // Пробный виртуальный бот (Волна 1): параметры фиксированы, подписка не нужна.
  const isVirtual          = !!bot?.virtual
  const virtualOrdersUsed  = useDashboardStore(s => s.virtualOrdersUsed)
  const virtualOrdersLimit = useDashboardStore(s => s.virtualOrdersLimit)
  const setVirtualOrders   = useDashboardStore(s => s.setVirtualOrders)
  const setActiveModal     = useDashboardStore(s => s.setActiveModal)

  const [isPending, setIsPending] = useState(false)
  const isActive = bot?.status === 'active'
  const isInitializing = bot?.status === 'initializing'
  const [localInitializing, setLocalInitializing] = useState(false)
  const subTypeAllowed = bot.accountMode === 'demo' ? demoAllowed : realAllowed
  // У пробного бота ползунок НЕ блокируется подпиской: запуск/остановка
  // доступны всегда (запуск ограничивает только лимит виртуальных сделок).
  const subLocked = !isAdmin && !subTypeAllowed && !isVirtual

  // Признак «карточка ещё смонтирована». Долгие ожидания инициализации бота
  // проверяют его, чтобы после ухода со страницы не продолжать поллить и не
  // звать setState на размонтированный компонент.
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  const [deleteState, setDeleteState] = useState(null)
  const [expanded, setExpanded]       = useState(false)
  const [editing, setEditing]         = useState(false)
  const [saveError, setSaveError]     = useState('')
  const [isSaving, setIsSaving]       = useState(false)
  const [stats, setStats]             = useState({ closedTrades: 0, totalProfit: 0 })
  const [isEnvReady, setIsEnvReady]   = useState(true)
  // Понятная юзеру ошибка по боту с бэкенда: { kind, at } либо null. Бэкенд
  // отдаёт только категорию — фразу подбирает словарь (см. problemText ниже),
  // поэтому в карточке нет ни кодов биржи, ни её JSON.
  const [problem, setProblem]         = useState(null)

  // Реальная ставка фандинга по паре бота — с бэкенда (кэш поллера funding.js),
  // а не из фейкового массива MOCK_SIGNALS. Если у бота нет пары (боты с
  // бэкенда её не хранят) или пара не в кэше — чип не показываем вовсе.
  const [funding, setFunding] = useState(null)

  useEffect(() => {
    let cancelled = false
    if (!bot.pair) return undefined
    botApi.getFundingRate(bot.pair)
      .then(data => {
        if (!cancelled && data?.rate != null) setFunding(data)
      })
      .catch(() => { /* пара могла выпасть из кэша — просто не показываем чип */ })
    return () => { cancelled = true }
  }, [bot.pair])

  const p = bot?.algoParams ?? null

  // Загрузка статистики закрытых сделок (вынесена в useCallback, чтобы
  // её можно было переиспользовать и в интервале, и сразу после
  // старта/остановки бота — не только один раз при монтировании).
  const fetchStats = useCallback(async () => {
    try {
      const data = await botApi.getBotStats(bot.id)
      setStats({
        closedTrades: data.closedTrades || 0,
        totalProfit: parseFloat(data.totalProfit || 0),
      })
      // Обновляем статус готовности окружения на основе RUN.log
      if (data.isEnvSetupComplete !== undefined) {
        setIsEnvReady(data.isEnvSetupComplete)
      }
      // Биржа отклоняет ордера (нет денег на торговом счёте, слишком большой
      // объём и т.п.) — бот работает, но сделок нет. Проблема приходит с
      // бэкенда категорией; исчезает сама, как только сделка откроется.
      setProblem(data.problem || null)
    } catch (err) {
      console.error(`[BotCard] Failed to fetch stats for bot ${bot.id}:`, err)
    }
  }, [bot.id])

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      if (isMounted) await fetchStats()
    }
    run()

    // Пока карточка открыта, периодически обновляем статистику —
    // сделки закрываются в фоне ботом, а не по действию пользователя,
    // поэтому одного запроса при монтировании недостаточно.
    const intervalId = setInterval(run, STATS_POLL_INTERVAL_MS)

    return () => {
      isMounted = false
      clearInterval(intervalId)
    }
  }, [fetchStats])

  // Пробный виртуальный бот: закрытые сделки добавляет планировщик в фоне,
  // поэтому периодически обновляем счётчик использованных виртуальных сделок —
  // чтобы плашка оплаты появлялась сразу после 2-й сделки, без перезагрузки.
  useEffect(() => {
    if (!bot?.virtual) return undefined
    let mounted = true
    const refresh = async () => {
      try {
        const me = await getMe()
        if (mounted && me) {
          setVirtualOrders({ used: me.virtual_orders_used, limit: me.virtual_orders_limit })
        }
      } catch { /* единичный сбой опроса не критичен */ }
    }
    refresh()
    const iv = setInterval(refresh, STATS_POLL_INTERVAL_MS)
    return () => { mounted = false; clearInterval(iv) }
  }, [bot?.virtual, setVirtualOrders])

  // ── Форма редактирования ──
  const [editLeverage,     setEditLeverage]     = useState(p?.leverage     ?? 2)
  const [editOrders,       setEditOrders]       = useState(p?.orders       ?? 1)
  const [editTradingMode,  setEditTradingMode]  = useState(p?.tradingMode  ?? 'safe')
  const [editGlobalVolume, setEditGlobalVolume] = useState(String(p?.globalVolume ?? ''))
  const [editTp,           setEditTp]           = useState(String(p?.tp           ?? ''))
  const [editSl,           setEditSl]           = useState(String(p?.sl           ?? ''))
  // В обоих полях порога лежит МОДУЛЬ значения, минус нарисован слева отдельным
  // знаком (2026-10-21): юзер вводит только цифру, знак не стирается.
  const [editMinFundingRate, setEditMinFundingRate] = useState(fundingMagnitude(bot.params?.MinFundingRate ?? '-0.6', '0.6'))
  // Макс. ставка финансирования (task.txt, 2026-09-16): НИЖНЯЯ граница диапазона
  // ставок. Пустое поле/undefined = ограничения нет (на сервер уходит null).
  const [editMaxFundingRate, setEditMaxFundingRate] = useState(fundingMagnitude(bot.params?.MaxFundingRate ?? ''))

  // Синхронизируем стейт формы с актуальными данными бота при каждом изменении bot
  // (без этого при повторном открытии формы редактирования оставались старые значения)
  useEffect(() => {
    const params = bot?.algoParams ?? {}
    setEditLeverage(params.leverage ?? 2)
    setEditOrders(params.orders ?? 1)
    setEditTradingMode(params.tradingMode ?? 'safe')
    setEditGlobalVolume(String(params.globalVolume ?? ''))
    setEditTp(String(params.tp ?? ''))
    setEditSl(String(params.sl ?? ''))
    setEditMinFundingRate(fundingMagnitude(bot.params?.MinFundingRate ?? '-0.6', '0.6'))
    setEditMaxFundingRate(fundingMagnitude(bot.params?.MaxFundingRate ?? ''))
  }, [bot])

  const handleSave = async () => {
    setSaveError('')
    setIsSaving(true)

    // Диапазон ставок (2026-10-28): нижняя граница (MaxFundingRate) обязана быть
    // глубже верхней (MinFundingRate). Иначе выборка пуста — бот активен, а
    // сделок нет; такую настройку не сохраняем (сервер тоже ответит 400).
    const range = parseThresholdRange({
      upper: fundingSigned(editMinFundingRate, -0.6),
      lower: fundingSigned(editMaxFundingRate, null),
    })
    if (!range.ok) {
      setSaveError(range.error)
      setIsSaving(false)
      haptic.error?.()
      return
    }

    const newParams = {
      leverage:     editLeverage,
      tradingMode:  editTradingMode,
      orders:       editOrders,
      globalVolume: Number(editGlobalVolume) || 0,
      tp:           Number(editTp) || 0,
      sl:           Number(editSl) || 0,
      // В поле лежит модуль — знак минуса возвращаем здесь (fundingSigned);
      // `|| -0.6` съедал порог 0 (Number(0) — ложное значение).
      minFundingRate: fundingSigned(editMinFundingRate, -0.6),
      // Макс. ставка: пустое поле → null (ограничения нет). 0 — валидное число,
      // поэтому пустота обрабатывается явно, а не через `||`.
      maxFundingRate: fundingSigned(editMaxFundingRate, null),
    }

    try {
      const result = await botApi.updateBot(bot.id, newParams)

      // ВАЖНО: обновляем локальный стор данными, которые сервер реально
      // записал и вернул (result.params), а не тем, что мы сами насчитали
      // на клиенте. Раньше здесь использовался newParams напрямую — из-за
      // этого интерфейс мог показывать "успешное" сохранение, даже если
      // сервер по какой-то причине не применил изменение (например, id
      // бота не нашёлся в bots_config.json). Теперь UI всегда отражает
      // именно то, что подтвердил сервер.
      const saved = result?.params
      const savedAlgoParams = saved
        ? {
            leverage: saved.Leverage,
            orders: saved.Token,
            globalVolume: saved.Volume,
            tp: saved.TakeProfit,
            sl: saved.StopLoss,
            tradingMode: saved.TradingMode ?? newParams.tradingMode,
          }
        : newParams

      updateUserBot(bot.id, { algoParams: savedAlgoParams })
      haptic.success()
      setEditing(false)
    } catch (err) {
      console.error('[BotCard] Save failed:', err)
      setSaveError(err?.message ?? t.connectionError)
      haptic.error?.()
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = () => {
    if (isActive) {
      haptic.warning()
      setDeleteState('hint')
      // Автоматически скрываем подсказку через 2 секунды
      setTimeout(() => setDeleteState(null), 2000)
      return
    }
    haptic.heavy()
    setDeleteState('confirm')
  }

  const profitColor = stats.totalProfit > 0 ? '#34D399' : stats.totalProfit < 0 ? '#FF3B30' : '#8E8E93'

  // Определяем режим торговли. По умолчанию считаем 'safe' (Системные),
  // если явно не указано 'custom'.
  const currentMode = bot.params?.TradingMode || bot.algoParams?.tradingMode || 'safe';
  const isCustomMode = currentMode === 'custom';
  
  // Жестко задаем текст, чтобы исключить ошибки в файлах перевода
  const tradingModeLabel = isCustomMode ? t.tradingModeCustom : t.tradingModeSystem;
  // Цвет "Пользовательские" теперь зелёный (#34D399), "Системные" — серый (#636366).
  const tradingModeColor = isCustomMode ? '#34D399' : '#636366';

  // Направление сделки: Long (сбор фандинга) / Short (сбор падений). У старых
  // ботов поля нет — считаем Long.
  const dirRaw = bot.params?.TradeDirection ||
    (bot.algoParams?.tradeMode ? (bot.algoParams.tradeMode === 'short' ? 'SHORT' : 'LONG') : '') ||
    'LONG'
  const direction = dirRaw.toUpperCase()

  const rows = [
    // Пробный виртуальный бот (п.4 task.txt): «демо-стратегия: бот торгует
    // виртуальными деньгами» не показываем — достаточно бейджа «ПРОБНЫЙ»
    // в шапке карточки.
    { label: t.addBotTradingMode, value: tradingModeLabel, valueColor: tradingModeColor },
    { label: t.addBotTradeDir, value: direction === 'SHORT' ? t.tradeModeShort : t.tradeModeLong, valueColor: direction === 'SHORT' ? '#F59E0B' : '#34D399' },
    { label: t.botBalance,  value: `${Number(p?.globalVolume ?? 0).toLocaleString('en', { minimumFractionDigits: 2 })} USDT` },
    {
      label: t.botProfit,
      value: formatPnlNoRound(stats.totalProfit),
      valueColor: profitColor,
    },
    { label: t.botClosedTrades, value: stats.closedTrades },
    {
      label: t.botStatus,
      value: (!isEnvReady) ? t.initEnv : ((isInitializing || localInitializing) ? t.initProgress : (isActive ? t.botStatusActive : t.botStatusPaused)),
      valueColor: (!isEnvReady) ? '#FF9500' : ((isInitializing || localInitializing) ? '#FF9500' : (isActive ? '#34D399' : '#FF3B30'))
    },
    { 
      label: t.accountMode,
      value: (bot.params?.DEMO === "True" || bot.params?.DEMO === true || bot.accountMode === 'demo') ? 'DEMO' : 'REAL', 
      valueColor: (bot.params?.DEMO === "True" || bot.params?.DEMO === true || bot.accountMode === 'demo') ? '#636366' : '#34D399' 
    },
    { label: t.botCreated,  value: formatDateTime(bot.createdAt) },
    { label: t.botExchange, value: bot.exchange || 'Bybit' },
  ]

  // Категория проблемы с бэкенда → пара «заголовок, совет» из словаря. Незнакомую
  // категорию (бэкенд новее фронта) показываем общей фразой, а не пустотой:
  // молчание там, где бот не торгует, — худший вариант.
  const problemText = problem
    ? ({
        volume:   [t.botProblemVolume,  t.botProblemVolumeAdvice],
        no_funds: [t.botProblemNoFunds, t.botProblemNoFundsAdvice],
        api_key:  [t.botProblemApiKey,  t.botProblemApiKeyAdvice],
        symbol:   [t.botProblemSymbol,  t.botProblemSymbolAdvice],
        other:    [t.botProblemOther,   t.botProblemOtherAdvice],
      }[problem.kind]) || [t.botProblemOther, t.botProblemOtherAdvice]
    : null

  // Подписка истекла и тип счёта бота недоступен → нажатие на заблокированный
  // ползунок показывает уведомление вместо безмолвного отказа.
  const handleSubExpiredPress = () => {
    haptic.medium()
    const tg = window.Telegram?.WebApp
    const message = t.botSubExpired
    if (tg?.showAlert) tg.showAlert(message)
    else alert(message)
  }

  return (
    <div style={{
      background: '#1A1A1A',
      borderRadius: 20,
      border: '1px solid rgba(255,255,255,0.04)',
      overflow: 'hidden',
    }}>
      {/* ── Collapsed header — always visible ── */}
      <div style={{ padding: '12px 14px 12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Toggle */}
        <BotToggle active={isActive} t={t} disabled={subLocked || (!isEnvReady || isInitializing || localInitializing || isPending)} onLockedPress={subLocked ? handleSubExpiredPress : undefined} onChange={async () => {
          if (isPending || isInitializing || localInitializing) return
          setIsPending(true)
          haptic.medium()

          // Пробный виртуальный бот: лимит виртуальных сделок исчерпан —
          // запуск невозможен, сразу показываем плашку оплаты.
          if (isVirtual && !isActive && virtualOrdersUsed >= virtualOrdersLimit) {
            setIsPending(false)
            setActiveModal('subscription')
            return
          }

          const action = isActive ? botApi.stopBot : botApi.startBot
          try {
            await action(bot.id)

            // Если запустили бот - устанавливаем локальный флаг инициализации
            // и блокируем ползунок до появления маркера в RUN.log
            if (!isActive) {
              setIsEnvReady(false)
              setLocalInitializing(true)
              
              // Ждём пока бот завершит инициализацию (пока сервер не вернёт status = 'active').
              // Ограниченная асинхронная попытка: максимум 120 проверок раз в 5с (10 минут),
              // с проверкой mountedRef — после ухода со страницы поллинг прекращается.
              let initialized = false
              let attempts = 0
              const maxAttempts = 120 // Максимум 120 попыток (10 минут при интервале 5 сек)

              while (!initialized && attempts < maxAttempts && mountedRef.current) {
                await new Promise(resolve => setTimeout(resolve, 5000))
                if (!mountedRef.current) break
                try {
                  const bots = await botApi.getBots()
                  if (!mountedRef.current) break
                  useDashboardStore.getState().setUserBots(bots)

                  const updatedBot = bots.find(b => b.id === bot.id)
                  if (updatedBot && updatedBot.status === 'active') {
                    initialized = true
                    setLocalInitializing(false)
                    console.log('Bot initialized!')
                  }
                } catch {
                  // Единичная ошибка опроса не прерывает ожидание — пробуем ещё раз.
                }
                attempts++
                // Логируем редко (раз в минуту), а не на каждую попытку.
                if (attempts % 12 === 0) console.log(`Init check ${attempts}/${maxAttempts}...`)
              }

              if (!initialized) {
                setLocalInitializing(false)
                if (mountedRef.current) console.log('Init timeout')
              }
            } else {
              const bots = await botApi.getBots()
              useDashboardStore.getState().setUserBots(bots)
            }

            fetchStats()
            setDeleteState(null)
          } catch (err) {
            console.error("Toggle failed:", err)
            haptic.error?.()
            setLocalInitializing(false)
            // Показываем юзеру причину отказа (например, лимит одновременно
            // работающих ботов). API отдаёт ошибку в виде "Server error 503:
            // {\"detail\":\"...\"}" — достаём detail, иначе показываем сырой текст.
            const tg = window.Telegram?.WebApp
            const m = String(err?.message || '').match(/"detail"\s*:\s*"([^"]+)"/)
            const message = m ? m[1] : (err?.message ? `⚠️ ${err.message}` : null)
            if (message) {
              if (tg?.showAlert) tg.showAlert(message)
              else alert(message)
            }
          } finally {
            setIsPending(false)
          }
        }} />

        {/* Tappable area: name + exchange + funding rate + profit */}
        <button
          onClick={() => { haptic.light(); setExpanded(e => !e) }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'left', minWidth: 0, minHeight: 44, padding: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, marginLeft: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{
                fontSize: 15, fontWeight: 700, color: '#FFF',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                minWidth: 0,
              }}>
                {bot.name}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
              {/* Пробный виртуальный бот (Волна 1): бейдж «ПРОБНЫЙ» — в строке
                  биржи, а не рядом с именем бота (не наезжает на имя). */}
              {isVirtual && (
                <span style={{
                  fontSize: 9, fontWeight: 700, color: '#FFB020',
                  background: 'rgba(255,176,32,0.12)',
                  border: '1px solid rgba(255,176,32,0.3)',
                  borderRadius: 5, padding: '2px 6px',
                  letterSpacing: 0.5, flexShrink: 0,
                }}>
                  {t.virtualBadge}
                </span>
              )}
              <span style={{ fontSize: 11, color: '#636366' }}>{bot.exchange || 'Bybit'}</span>
              {funding && (
                <>
                  <span style={{ fontSize: 10, color: '#3A3A3A' }}>·</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    color: funding.side === 'long' ? '#34D399' : '#FF3B30',
                  }}>
                    {funding.side === 'long' ? '+' : '-'}{funding.rate}%
                  </span>
                  <span style={{ fontSize: 10, color: '#636366' }}>
                    ⏱ {formatTime(funding.minutesToFunding, t)}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* ФИКС: раньше здесь использовалось устаревшее bot.profit — цифра
              не совпадала с цветом, который уже брался из stats.totalProfit.
              Теперь обе величины берутся из реальной статистики PNL-файла. */}
          <span style={{ fontSize: 14, fontWeight: 700, flexShrink: 0, color: profitColor }}>
            {formatPnlNoRound(stats.totalProfit)}
          </span>
        </button>

        {/* Expand/collapse chevron */}
        <button
          onClick={() => { haptic.light(); setExpanded(e => !e) }}
          style={{
            width: 44, height: 44, borderRadius: 10,
            background: 'rgba(255,255,255,0.06)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded
            ? <ChevronUp size={16} color="#636366" />
            : <ChevronDown size={16} color="#636366" />
          }
        </button>
      </div>

      {/* Ошибка по боту — понятным юзеру языком. Показывается, не разворачивая
          карточку: иначе «бот включён, а сделок нет» остаётся без объяснения.
          Приходит с бэкенда только у включённого бота и пропадает сам, как
          только сделка откроется. */}
      {problemText && (
        <div style={{
          margin: '0 14px 12px', padding: '9px 11px', borderRadius: 12,
          background: 'rgba(255,149,0,0.10)',
          border: '1px solid rgba(255,149,0,0.25)',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <span style={{ fontSize: 13, lineHeight: '16px' }}>⚠️</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#FF9500', lineHeight: '16px' }}>
              {problemText[0]}
            </div>
            <div style={{ fontSize: 11, color: '#8E8E93', marginTop: 3, lineHeight: 1.35 }}>
              {problemText[1]}
            </div>
          </div>
        </div>
      )}

      {/* ── Expanded content ── */}
      {expanded && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '10px 16px 14px' }}>
          {/* Action row: gear (left) + delete (right) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            {/* Gear button. У пробного виртуального бота параметры фиксированы —
                шестерёнку не показываем вовсе (см. POST /api/bots isVirtual). */}
            {!isVirtual && <button
              onClick={() => !isActive && setEditing(e => !e)}
              disabled={isActive}
              style={{
                width: 44, height: 44, borderRadius: 12,
                background: isActive ? 'rgba(255,255,255,0.02)' : (editing ? 'rgba(255,107,53,0.18)' : 'rgba(255,255,255,0.06)'),
                border: isActive ? '1px solid rgba(255,255,255,0.02)' : (editing ? '1px solid rgba(255,107,53,0.3)' : '1px solid transparent'),
                cursor: isActive ? 'not-allowed' : 'pointer',
                opacity: isActive ? 0.4 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.2s',
              }}
              aria-label="Edit bot"
            >
              <Settings size={17} color={isActive ? '#3A3A3A' : (editing ? '#34D399' : '#636366')} />
            </button>}

            {/* Delete area */}
            {deleteState === 'hint' ? (
              <span style={{
                fontSize: 11, color: '#FF3B30', fontWeight: 700,
                background: 'rgba(255,59,48,0.1)',
                border: '1px solid rgba(255,59,48,0.2)',
                borderRadius: 8, padding: '5px 9px',
              }}>
                {t.botStopFirst}
              </span>
            ) : (
              <button
                onClick={handleDeleteClick}
                disabled={isActive}
                style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: isActive ? 'rgba(255,59,48,0.03)' : 'rgba(255,59,48,0.1)',
                  border: 'none',
                  cursor: isActive ? 'not-allowed' : 'pointer',
                  opacity: isActive ? 0.3 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  WebkitTapHighlightColor: 'transparent',
                }}
                aria-label="Delete bot"
              >
                <Trash2 size={17} color={isActive ? '#3A3A3A' : "#FF3B30"} />
              </button>
            )}
          </div>

          {/* ── Edit form ── */}
          {editing && (
            <div style={{
              marginBottom: 12, padding: '14px',
              borderRadius: 14, background: 'rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,107,53,0.12)',
            }}>
              {/* Leverage */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#8E8E93', fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t.addBotLeverage}
                </label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  {LEVERAGE_OPTIONS.map(lv => (
                    <button key={lv} type="button" onClick={() => setEditLeverage(lv)} style={{
                      flex: 1, padding: '10px 0', borderRadius: 10,
                      fontSize: 13, fontWeight: 700,
                      background: editLeverage === lv
                        ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                        : 'rgba(255,255,255,0.06)',
                      color: editLeverage === lv ? '#fff' : '#636366',
                      border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                      {lv}x
                    </button>
                  ))}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number" inputMode="numeric"
                    value={editLeverage}
                    onChange={e => {
                      const n = parseInt(e.target.value)
                      if (!isNaN(n)) setEditLeverage(Math.min(10, Math.max(1, n)))
                    }}
                    min={1} max={10}
                    style={{ ...inputStyle, paddingRight: 50, textAlign: 'center' }}
                  />
                  <div style={{
                    position: 'absolute', right: 2, top: 2, bottom: 2, width: 44,
                    display: 'flex', flexDirection: 'column', borderRadius: 10, overflow: 'hidden',
                  }}>
                    <button
                      type="button"
                      onClick={() => setEditLeverage(v => Math.min(10, v + 1))}
                      style={{
                        flex: 1, background: 'rgba(255,255,255,0.07)',
                        border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <ChevronUp size={13} color="#8E8E93" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditLeverage(v => Math.max(1, v - 1))}
                      style={{
                        flex: 1, background: 'rgba(255,255,255,0.07)',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <ChevronDown size={13} color="#8E8E93" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Orders count */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#8E8E93', fontWeight: 700, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t.addBotOrders}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {ORDER_COUNTS.map(n => (
                    <button key={n} type="button" onClick={() => setEditOrders(n)} style={{
                      flex: 1, padding: '10px 0', borderRadius: 10,
                      fontSize: 14, fontWeight: 700,
                      background: editOrders === n
                        ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                        : 'rgba(255,255,255,0.06)',
                      color: editOrders === n ? '#fff' : '#636366',
                      border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Global volume */}
              <div style={{ marginBottom: 12 }}>
                {subLabel(t.addBotGlobalVolume)}
                <input
                  type="number" inputMode="decimal"
                  value={editGlobalVolume}
                  onChange={e => setEditGlobalVolume(e.target.value)}
                  placeholder="0" min={0} step={100}
                  style={inputStyle}
                />
              </div>

              {/* TP + SL */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div>
                  {subLabel(t.addBotTakeProfit, '#34D399')}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number" inputMode="decimal"
                      value={editTp}
                      onChange={e => setEditTp(e.target.value)}
                      step="0.01" placeholder="0.5" min={0}
                      disabled={currentMode === 'safe'}
                      style={{ 
                        ...inputStyle, paddingRight: 28, 
                        border: '1px solid rgba(52,199,89,0.35)',
                        opacity: currentMode === 'safe' ? 0.5 : 1,
                        cursor: currentMode === 'safe' ? 'not-allowed' : 'text'
                      }}
                    />
                    <span style={{ ...pctSuffix, color: '#34D399' }}>%</span>
                  </div>
                </div>
                <div>
                  {subLabel(t.addBotStopLoss, '#FF3B30')}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number" inputMode="decimal"
                      value={editSl}
                      onChange={e => setEditSl(e.target.value)}
                      step="0.01" placeholder="1.0" min={0}
                      disabled={currentMode === 'safe'}
                      style={{ 
                        ...inputStyle, paddingRight: 28, 
                        border: '1px solid rgba(255,59,48,0.35)',
                        opacity: currentMode === 'safe' ? 0.5 : 1,
                        cursor: currentMode === 'safe' ? 'not-allowed' : 'text'
                      }}
                    />
                    <span style={{ ...pctSuffix, color: '#FF3B30' }}>%</span>
                  </div>
                </div>
              </div>

              {/* Min Funding Rate — в режиме системных настроек (safe) НЕ редактируется */}
              <div style={{ marginBottom: 12 }}>
                {subLabel(t.funding, '#8E8E93')}
                <div style={{ position: 'relative' }}>
                  <span style={{ ...minusPrefix, opacity: currentMode === 'safe' ? 0.5 : 1 }}>−</span>
                  <input
                    type="text" inputMode="decimal"
                    value={editMinFundingRate}
                    onChange={e => setEditMinFundingRate(fundingDraft(e.target.value))}
                    placeholder="0.6"
                    disabled={currentMode === 'safe'}
                    style={{
                      ...inputStyle, paddingLeft: 26, paddingRight: 28,
                      opacity: currentMode === 'safe' ? 0.5 : 1,
                      cursor: currentMode === 'safe' ? 'not-allowed' : 'text',
                    }}
                  />
                  <span style={{ ...pctSuffix, color: '#8E8E93' }}>%</span>
                </div>
              </div>

              {/* Max Funding Rate (task.txt, 2026-09-16): необязательная НИЖНЯЯ
                  граница диапазона ставок. Пусто = ограничения нет. */}
              <div style={{ marginBottom: 12 }}>
                {subLabel(t.addBotMaxFunding, '#8E8E93')}
                <div style={{ position: 'relative' }}>
                  <span style={{ ...minusPrefix, opacity: currentMode === 'safe' ? 0.5 : 1 }}>−</span>
                  <input
                    type="text" inputMode="decimal"
                    value={editMaxFundingRate}
                    onChange={e => setEditMaxFundingRate(fundingDraft(e.target.value))}
                    placeholder="0.8"
                    disabled={currentMode === 'safe'}
                    style={{
                      ...inputStyle, paddingLeft: 26, paddingRight: 28,
                      opacity: currentMode === 'safe' ? 0.5 : 1,
                      cursor: currentMode === 'safe' ? 'not-allowed' : 'text',
                    }}
                  />
                  <span style={{ ...pctSuffix, color: '#8E8E93' }}>%</span>
                </div>
              </div>

              {/* Save button */}
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: '100%', padding: '13px', borderRadius: 12,
                  fontSize: 14, fontWeight: 700,
                  background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
                  color: '#fff', border: 'none',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {isSaving ? '...' : t.save}
              </button>

              {saveError && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#FF3B30', textAlign: 'center' }}>
                  {saveError}
                </p>
              )}
            </div>
          )}

          {/* Info rows */}
          <div>
            {rows.map((row, i) => (
              <InfoRow
                key={row.label}
                label={row.label}
                value={row.value}
                valueColor={row.valueColor}
                last={i === rows.length - 1 && !p}
              />
            ))}
          </div>

          {/* Algo params display — ВСЕГДА показываем секцию, даже если algoParams
              по какой-то причине не пришёл с бэкенда (раньше блок исчезал целиком) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 4px' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#636366', letterSpacing: 0.6, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              {t.addBotAlgoTitle}
            </span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
          </div>

          <InfoRow label={t.addBotLeverage}    value={`${p?.leverage ?? 2}x`} />
          <InfoRow label={t.addBotOrders}      value={p?.orders ?? 1} />
          <InfoRow
            label={t.addBotTakeProfit}
            value={`${p?.tp ?? 0}%`}
            valueColor="#34D399"
          />
          <InfoRow
            label={t.addBotStopLoss}
            value={`${p?.sl ?? 0}%`}
            valueColor="#FF3B30"
          />
          <InfoRow
            label={t.funding}
            value={`${bot.params?.MinFundingRate ?? '-0.6'}%`}
            valueColor="#8E8E93"
            last={!(bot.params?.MaxFundingRate != null && bot.params?.MaxFundingRate !== '')}
          />
          {/* Макс. ставка (task.txt, 2026-09-16): показываем только если задана
              (иначе поле пустое = ограничения нет, и строка лишняя) */}
          {bot.params?.MaxFundingRate != null && bot.params?.MaxFundingRate !== '' && (
            <InfoRow
              label={t.addBotMaxFunding}
              value={`${bot.params.MaxFundingRate}%`}
              valueColor="#8E8E93"
              last
            />
          )}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteState === 'confirm' && (
        <ConfirmModal
          title={t.botConfirmDel}
          message={t.botConfirmDelMsg}
          confirmLabel={t.botDelete}
          cancelLabel={t.botCancel}
          onConfirm={async () => {
            // Сразу убираем бота из интерфейса
            removeUserBot(bot.id)
            setDeleteState(null)
            haptic.success()

            // Отправляем запрос на сервер в фоне
            try {
              await botApi.deleteBot(bot.id)
            } catch (err) {
              console.error("Ошибка при удалении на сервере:", err)
              // Если сервер вернет ошибку, бот уже исчез с экрана.
              // В будущем можно добавить повторную синхронизацию.
            }
          }}
          onCancel={() => setDeleteState(null)}
        />
      )}
    </div>
  )
}
